import React, { useEffect, useState, useRef, useCallback } from 'react'
import { Search, type StrictSearchProps } from 'semantic-ui-react'
import classnames from 'classnames/bind'
import Icon from '../../elementals/icon'
import useIsMounted from '../../../hooks/useIsMounted'
import { formFieldWrapper } from '../formFieldWrapper'
// @ts-ignore cannot find type declarations
import styles from './styles.scss'

// @ts-ignore unsafe assignment of any
const cx = classnames.bind(styles)

export interface ISearchFieldProps extends StrictSearchProps {
  elmId: string
  onSearchIconClick?: () => void
  showError?: boolean
  hideWrapper?: boolean
  stickyOpen?: boolean
  compact: boolean
  label?: string
  disabled?: boolean
  deleteIconEnabled?: boolean
  viewMode?: boolean
}

/**
 * SearchWrapper is used primarily to hold state for implementing "stickyOpen" feature.
 * If stickyOpen is not in use, all props are passed through as is.
 * TODO: add checks for all other actions when disabled
 */
const SearchWrapper = ({
  elmId,
  stickyOpen,
  deleteIconEnabled,
  loading,
  disabled,
  compact,
  showError, // eslint-disable-line @typescript-eslint/no-unused-vars -- don't need this
  onResultSelect: onResultSelectOrig,
  onFocus: onFocusOrig,
  onSearchIconClick: onSearchIconClickOrig,
  onSearchChange: onSearchChangeOrig,
  onBlur,
  placeholder,
  label,
  className = '',
  value: initialValue = '',
  results,
  ...rest
}: ISearchFieldProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [value, setValue] = useState(initialValue)
  const [searchChanging, setSearchChanging] = useState(false)

  const isMounted = useIsMounted()

  const inputRef = useRef<HTMLInputElement>()

  const open = useCallback<NonNullable<ISearchFieldProps['onFocus']>>(() => {
    inputRef.current?.select()
    setIsOpen(true)
  }, [])

  const close = useCallback<NonNullable<ISearchFieldProps['onBlur']>>(() => {
    setIsOpen(false)
  }, [])

  const onSearchChange = useCallback<
    NonNullable<ISearchFieldProps['onSearchChange']>
  >((_, { value }) => {
    setSearchChanging(true)

    setValue(typeof value !== 'undefined' ? value : '')
  }, [])

  const onSearchIconClick = useCallback(() => {
    if (!disabled) {
      if (onSearchIconClickOrig) {
        onSearchIconClickOrig()
      } else {
        // if no custom handler, just focus on input
        if (inputRef.current) {
          inputRef.current.focus()
        }
      }
    }
  }, [disabled, onSearchIconClickOrig])

  const onDeleteIconClick = useCallback(() => {
    if (disabled) {
      return
    }

    setValue('')
    setSearchChanging(false)
    setIsOpen(false)

    // NOTE:  Maybe<problem> 乁{°〰°}ㄏ
    onSearchChangeOrig?.(
      // @ts-ignore React.MouseEvent<HTMLElement, MouseEvent> doesn't match type
      {},
      {
        value: '',
      }
    )
  }, [disabled, onSearchChangeOrig])

  const scrollToTop = useCallback(() => {
    if (isMounted) {
      const menu = document.querySelector(
        '.ui.search.active.visible .results.visible'
      )
      if (menu) menu.scrollTop = 0
    }
  }, [isMounted])

  const onFocus = useCallback<NonNullable<ISearchFieldProps['onFocus']>>(
    (e, data) => {
      inputRef.current?.select()
      onFocusOrig?.(e, data)
    },
    [onFocusOrig]
  )

  useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  useEffect(() => {
    if (results?.length && searchChanging) {
      scrollToTop()
      setSearchChanging(false)
    }
  }, [results?.length, searchChanging, scrollToTop])

  const stickyOpenProps = stickyOpen
    ? {
        onFocus: open,
        onBlur: close,
        open: isOpen,
      }
    : {}

  const showDeleteIcon =
    deleteIconEnabled && !loading && !disabled && value?.length > 0

  const showDeleteIconClassName = cx('show-delete-icon')

  // @ts-ignore something something about this context
  const newClassName = classnames({
    compact,
    [className]: true,
    [showDeleteIconClassName]: showDeleteIcon,
  })

  const onResultSelect = useCallback<
    Required<ISearchFieldProps>['onResultSelect']
  >(
    (e, data) => {
      onResultSelectOrig?.(e, data)
      if (!stickyOpen) {
        inputRef.current?.blur()
      }
    },
    [onResultSelectOrig, stickyOpen]
  )
  return (
    <div className={cx('search')}>
      <Search
        aria-label={`${elmId}_search`}
        data-elm-id={`${elmId}_search`}
        className={newClassName}
        icon={
          <>
            {showDeleteIcon && (
              <Icon
                className={cx('link', 'delete-icon')}
                data-elm-id={`${elmId}_delete_icon`}
                name='xmark'
                onClick={onDeleteIconClick}
                iconStyle='regular'
                size='medium'
              />
            )}
            <Icon
              className={cx({ link: !disabled })}
              data-elm-id={`${elmId}_search_icon`}
              name='search'
              onClick={onSearchIconClick}
              type='submit'
            />
          </>
        }
        loading={loading}
        disabled={disabled}
        placeholder={placeholder || label}
        input={{ input: { ref: inputRef } }}
        {...rest}
        onBlur={onBlur}
        onFocus={onFocus}
        {...stickyOpenProps}
        onSearchChange={(e, data) => {
          onSearchChangeOrig?.(e, data)
          onSearchChange(e, data)
        }}
        results={results}
        onResultSelect={onResultSelect}
        autoComplete='off'
        role='presentation'
        value={value}
      />
    </div>
  )
}

export default formFieldWrapper(
  (fieldProps: ISearchFieldProps, showError: boolean) => (
    <SearchWrapper {...fieldProps} showError={showError} />
  )
)
